/*
  assets/sc-hero-slides.js
  トップの告知の帯（snippets/sc-hero-slides.liquid）を横へ送る。W3C APG の「自動で送るカルーセル」の型:
  操作部品（停止/再生・前・次）を枚より先に置き、送っている間は aria-live を off、止まったら polite にする。今の枚以外は inert。
  送る条件: 再生中・マウスが乗っていない・帯の半分以上が見えている・タブが表。1周して始めの枚に戻ったら止まる。
  止める（その閲覧では自動で再開しない。再開は再生のボタンだけ）: 枚に触れた・前/次を押した・キーボードで焦点が入った。
  動きを減らす設定の人には最初から止めておく（送る時の動きも CSS で消す）。
  スワイプは指とペンだけ（横に8px動いたら指に付いて動く・40pxで次/前・端では戻る）。直後の click は取り消す。
  文が写真の上に収まらない時（文字を大きくした時など）は、文を写真の下へ回す（.is-sc-stacked）。
  [2026-09-30 新設] 案A（ボス「新しい見た目でもスライドしてほしい」）
*/
(() => {
  const init = (root) => {
    if (!root || root.dataset.scHeroReady) return;
    root.dataset.scHeroReady = 'true';
    const track = root.querySelector('[data-sc-hero-track]');
    const controls = root.querySelector('[data-sc-hero-controls]');
    // 5倍デーの枚は、sc-point5x-script.liquid が先に消していることがある。今ある枚で数え直す
    const slides = Array.from(track.querySelectorAll('[data-sc-hero-slide]'));
    const total = slides.length;
    if (!total) return;
    slides.forEach((slide, i) => slide.setAttribute('aria-label', `${total}枚中${i + 1}枚目`));
    const texts = slides.map((slide) => slide.querySelector('.sc-heroSlides__text')).filter(Boolean);
    const media = slides[0].querySelector('.sc-heroSlides__media');
    let rotateButton = null;
    if (total < 2 || !controls) {
      if (controls) controls.remove();
    } else {
      controls.hidden = false;
      rotateButton = controls.querySelector('[data-sc-hero-rotate]');
    }

    // 文が写真の上に収まるか: 停止のボタンの見える円（箱の上から4px）の8px上まで。ボタンが無ければ写真の下端の8px上まで
    const narrow = window.matchMedia('(max-width: 23.43em)');
    let stackedWidth = 0;
    const getLimit = () => (rotateButton ? rotateButton.getBoundingClientRect().top + 4 - 8 : media.getBoundingClientRect().bottom - 8);
    // [2026-10-02 スタッフ要望・ボス「自動で表示に合わせて」] 幅768px以上の5倍デーの枚は、日付の文を写真の左の空きに収まる
    // いちばん大きい字にする（改行の位置で高さが段になって変わるので、比例の CSS だけでは詰まる幅と空く幅が出た）。
    // 下は停止のボタンの円のさらに20px上まで（ボタンに詰まって見えないように）・横は文の枠を越えない。16px でも収まらない時は CSS の既定に戻し、下の fit が写真の下へ回す
    const wide = window.matchMedia('(min-width: 768px)');
    const p5 = slides.find((slide) => slide.hasAttribute('data-sc-point5x-slide'));
    const p5Text = p5 && p5.querySelector('.sc-heroSlides__text');
    const p5Detail = p5 && p5.querySelector('.sc-heroSlides__detail');
    const fitSize = () => {
      if (!p5Detail) return;
      p5Detail.style.fontSize = '';
      if (!wide.matches) return;
      const limit = getLimit() - 20;
      const fits = (px) => {
        p5Detail.style.fontSize = `${px}px`;
        return p5Text.getBoundingClientRect().bottom <= limit && p5Text.scrollWidth <= p5Text.clientWidth + 1;
      };
      let lo = 16;
      let hi = Math.max(lo, Math.min(56, root.clientWidth * 0.07));
      if (!fits(lo)) {
        p5Detail.style.fontSize = '';
        return;
      }
      for (let i = 0; i < 10; i += 1) {
        const mid = (lo + hi) / 2;
        if (fits(mid)) lo = mid;
        else hi = mid;
      }
      p5Detail.style.fontSize = `${Math.floor(lo * 2) / 2}px`;
    };
    const fit = () => {
      if (narrow.matches) {
        root.classList.remove('is-sc-stacked');
        if (p5Detail) p5Detail.style.fontSize = '';
        return;
      }
      if (root.classList.contains('is-sc-stacked')) {
        if (root.clientWidth === stackedWidth) return;
        root.classList.remove('is-sc-stacked');
      }
      fitSize();
      const limit = getLimit();
      if (texts.some((text) => text.getBoundingClientRect().bottom > limit)) {
        root.classList.add('is-sc-stacked');
        stackedWidth = root.clientWidth;
      }
    };
    fit();
    window.addEventListener('resize', fit);
    if (document.fonts && document.fonts.ready) document.fonts.ready.then(fit);

    // 読み込みが終わったら、残りの枚の写真も読む（送った時に白い枠を見せない）
    const loadAll = () => {
      track.querySelectorAll('img[loading="lazy"]').forEach((img) => {
        img.loading = 'eager';
      });
    };
    if (document.readyState === 'complete') loadAll();
    else window.addEventListener('load', loadAll, { once: true });

    if (!rotateButton) return;

    const current = controls.querySelector('[data-sc-hero-current]');
    controls.querySelector('[data-sc-hero-total]').textContent = String(total);
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)');
    let index = 0;
    let playing = false;
    let hovered = false;
    let held = false; // [2026-10-01] トップの検索の候補が開いている間（assets/search.js の sc:search-suggest）
    let inView = false;
    let steps = 0;
    let timer = 0;

    const show = (next) => {
      index = (next + total) % total;
      track.style.setProperty('--sc-hero-index', String(index));
      slides.forEach((slide, i) => slide.toggleAttribute('inert', i !== index));
      current.textContent = String(index + 1);
    };
    const interval = () => {
      const seconds = Number(slides[index].dataset.scSeconds) || 6;
      return Math.min(12, Math.max(4, seconds)) * 1000;
    };
    const schedule = () => {
      window.clearTimeout(timer);
      if (!playing || hovered || held || !inView || document.visibilityState !== 'visible') return;
      timer = window.setTimeout(() => {
        show(index + 1);
        steps += 1;
        // 1周して、始めの枚に戻ったら止まる
        if (steps >= total) setPlaying(false);
        else schedule();
      }, interval());
    };
    const setPlaying = (value) => {
      playing = value;
      rotateButton.setAttribute('aria-label', value ? '自動再生を停止' : '自動再生を開始');
      rotateButton.classList.toggle('is-sc-paused', !value);
      track.setAttribute('aria-live', value ? 'off' : 'polite');
      schedule();
    };

    rotateButton.addEventListener('click', () => {
      if (playing) {
        setPlaying(false);
      } else {
        steps = 0;
        setPlaying(true);
      }
    });
    controls.querySelector('[data-sc-hero-prev]').addEventListener('click', () => {
      setPlaying(false);
      show(index - 1);
    });
    controls.querySelector('[data-sc-hero-next]').addEventListener('click', () => {
      setPlaying(false);
      show(index + 1);
    });
    root.addEventListener('focusin', (event) => {
      let keyboard = true;
      try {
        keyboard = event.target.matches(':focus-visible');
      } catch (error) {
        // :focus-visible を知らないブラウザは止める側に倒す
      }
      if (keyboard && playing) setPlaying(false);
    });
    root.addEventListener('pointerenter', (event) => {
      if (event.pointerType !== 'mouse') return;
      hovered = true;
      schedule();
    });
    root.addEventListener('pointerleave', (event) => {
      if (event.pointerType !== 'mouse') return;
      hovered = false;
      schedule();
    });
    document.addEventListener('visibilitychange', schedule);
    document.addEventListener('sc:search-suggest', (event) => {
      held = Boolean(event.detail && event.detail.open);
      schedule();
    });
    if ('IntersectionObserver' in window) {
      new IntersectionObserver(
        (entries) => {
          inView = entries[entries.length - 1].intersectionRatio >= 0.5;
          schedule();
        },
        { threshold: 0.5 },
      ).observe(root);
    } else {
      inView = true;
    }
    if (reduce.addEventListener) {
      reduce.addEventListener('change', () => {
        if (reduce.matches && playing) setPlaying(false);
      });
    }

    // スワイプ（指とペン）。縦のスクロールはブラウザに任せる（CSS の touch-action: pan-y）
    let pointer = null;
    let startX = 0;
    let startY = 0;
    let dragging = false;
    let suppressClick = false;
    track.addEventListener('pointerdown', (event) => {
      suppressClick = false;
      if (playing) setPlaying(false);
      if (event.pointerType === 'mouse') return;
      pointer = event.pointerId;
      startX = event.clientX;
      startY = event.clientY;
      dragging = false;
    });
    track.addEventListener('pointermove', (event) => {
      if (event.pointerId !== pointer) return;
      const dx = event.clientX - startX;
      if (!dragging) {
        if (Math.abs(dx) < 8 || Math.abs(dx) <= Math.abs(event.clientY - startY)) return;
        dragging = true;
        track.classList.add('is-sc-dragging');
        try {
          track.setPointerCapture(pointer);
        } catch (error) {
          // 捕まえられなくても動かせる
        }
      }
      const atEdge = (index === 0 && dx > 0) || (index === total - 1 && dx < 0);
      track.style.setProperty('--sc-hero-drag', `${atEdge ? dx / 3 : dx}px`);
    });
    const endDrag = (event, cancelled) => {
      if (event.pointerId !== pointer) return;
      pointer = null;
      if (!dragging) return;
      dragging = false;
      suppressClick = true;
      track.classList.remove('is-sc-dragging');
      track.style.setProperty('--sc-hero-drag', '0px');
      const dx = event.clientX - startX;
      if (cancelled || Math.abs(dx) < 40) return;
      if (dx < 0 && index < total - 1) show(index + 1);
      else if (dx > 0 && index > 0) show(index - 1);
    };
    track.addEventListener('pointerup', (event) => endDrag(event, false));
    track.addEventListener('pointercancel', (event) => endDrag(event, true));
    // スワイプの直後の click は行き先へ進ませない（計測の click より先に止めるため window の捕捉で受ける）
    window.addEventListener(
      'click',
      (event) => {
        if (!suppressClick || !track.contains(event.target)) return;
        suppressClick = false;
        event.preventDefault();
        event.stopPropagation();
      },
      true,
    );

    // テーマエディタで枚を選んだら、その枚を見せて止める
    document.addEventListener('shopify:block:select', (event) => {
      const i = slides.findIndex((slide) => slide === event.target || slide.contains(event.target));
      if (i === -1) return;
      setPlaying(false);
      show(i);
    });

    show(0);
    setPlaying(!reduce.matches);
  };

  init(document.querySelector('[data-sc-hero-slides]'));
  // テーマエディタで区画を読み直した時
  document.addEventListener('shopify:section:load', (event) => {
    init(event.target.querySelector('[data-sc-hero-slides]'));
  });
})();
